import { Category, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAppLocaleContext } from './locale_provider';

type CategoriesContextType = {
  items: Category[];
  fetchData: () => Promise<ServiceResponse<Category[]>>;
  isLoading: boolean;
  error: ErrorResponse | null;
};

const CategoriesContext = createContext({
  items: [],
  fetchData: async () => ({ type: 'client-error', message: 'Not implemented' }),
  isLoading: false,
  error: null,
} as CategoriesContextType);

export function useCategoriesContext() {
  return useContext(CategoriesContext);
}

export function CategoriesProvider({ children, config }: { children: React.ReactNode; config: string }) {
  const [items, setItems] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();
  const { localeId } = useAppLocaleContext();

  const fetchData = async (): Promise<ServiceResponse<Category[]>> => {
    setIsLoading(true);
    const response = await bzbsService.categoryApi.categories({
      config: config,
      byConfig: true,
      options: {
        device_locale: localeId,
        locale: localeId,
      },
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setItems(response.model);
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <CategoriesContext.Provider
      value={{
        items,
        fetchData,
        isLoading,
        error,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
}
